import { useState } from "react";
import { Link as RouterLink } from "react-router";
import { motion } from "framer-motion";
import { Play, Pause, MoreHorizontal, Link, ExternalLink, Layers, Star, Share2, QrCode } from "lucide-react";
import type { ComboResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { cn, formatPlayCount } from "@/lib/utils";
import { shareLink } from "@/lib/share";
import { QrModal } from "@/components/qr-modal";

interface ComboCardProps {
  combo: ComboResponse;
  index?: number;
  compact?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onPlay?: () => void;
}

export function ComboCard({ combo, index, compact, isFavorite, onToggleFavorite, onPlay }: ComboCardProps) {
  const { playCombo, activeComboId, stopAll } = useAudio();
  const playing = activeComboId === combo.id;
  const [showMenu, setShowMenu] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const handlePlay = () => {
    if (playing) {
      stopAll();
      return;
    }
    playCombo(
      combo.id,
      combo.name,
      combo.sounds.map((s) => ({
        id: s.id,
        audioUrl: s.audio_url,
        title: s.title,
        durationMs: s.duration_ms,
        slug: s.slug,
      })),
    );
    onPlay?.();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${combo.slug}`);
    setShowMenu(false);
  };

  const handleShare = () => {
    shareLink(`${window.location.origin}/c/${combo.slug}`, `${combo.name} · Wizard Sounds`);
    setShowMenu(false);
  };

  return (
    <motion.div
      className={cn(
        "group relative h-full bg-slate border border-mist rounded-xl sm:rounded-[18px] transition-all duration-200 ease-out",
        showMenu && "z-20",
        playing && "border-spark shadow-[0_12px_32px_-8px_rgba(255,182,73,0.18)]",
        !playing && "hover:border-spark hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-8px_rgba(255,182,73,0.18)]",
        compact ? "p-2 sm:p-3" : "p-2 sm:p-3 md:p-[18px]",
      )}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.12 }}
      onContextMenu={(e) => {
        e.preventDefault();
        setShowMenu(!showMenu);
      }}
    >
      <button
        onClick={handlePlay}
        className="flex w-full items-start gap-2 pr-7 text-left sm:gap-3 sm:pr-8"
      >
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full transition-colors duration-150",
            "h-7 w-7 sm:h-10 sm:w-10",
            playing ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft",
          )}
        >
          {playing ? (
            <Pause className="h-2.5 w-2.5 text-ink sm:h-[14px] sm:w-[14px]" />
          ) : (
            <Play className="h-2.5 w-2.5 text-ink ml-0.5 sm:h-[14px] sm:w-[14px]" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {index !== undefined && (
              <span className="font-mono text-[10px] text-fog tracking-wider">
                {index + 1}
              </span>
            )}
            <h3
              className={cn(
                "truncate font-sans font-medium",
                compact ? "text-xs sm:text-sm" : "text-xs sm:text-[15px]",
              )}
            >
              {combo.name}
            </h3>
          </div>

          <div className="mt-0.5 font-mono text-[9px] text-fog tracking-wide sm:mt-1.5 sm:text-[10px] md:text-[11px]">
            {formatPlayCount(combo.play_count)} plays &middot; {combo.sounds.length} sounds
          </div>
        </div>
      </button>

      {/* Combo badge — icon-only on mobile, icon + text on desktop */}
      <span className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-spark/12 p-1 sm:bottom-2 sm:right-2 sm:px-1.5 sm:py-0.5">
        <Layers className="h-2.5 w-2.5 text-spark" />
        <span className="hidden font-mono text-[8px] font-bold uppercase tracking-wider text-spark sm:inline">
          combo
        </span>
      </span>

      <div className="absolute right-2 top-2 flex items-center gap-0.5 sm:right-3 sm:top-3">
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            className={cn(
              "rounded-lg p-1 sm:p-1.5 transition-colors",
              isFavorite
                ? "text-spark"
                : "text-fog hover:text-spark sm:opacity-0 sm:group-hover:opacity-100",
            )}
          >
            <Star className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4", isFavorite && "fill-spark")} />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="rounded-lg p-1 text-fog hover:bg-slate-raised sm:p-1.5 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
        >
          <MoreHorizontal className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
      </div>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
          <div className="absolute left-0 right-0 top-10 z-50 rounded-xl border border-mist bg-slate-raised p-1 shadow-lg sm:left-auto sm:right-3 sm:top-12 sm:w-48 sm:rounded-[18px]">
            <button
              onClick={handleShare}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <Share2 className="h-4 w-4 text-fog" /> Share
            </button>
            <button
              onClick={() => {
                setShowQr(true);
                setShowMenu(false);
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <QrCode className="h-4 w-4 text-fog" /> QR code
            </button>
            <RouterLink
              to={`/c/${combo.slug}`}
              onClick={() => setShowMenu(false)}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <ExternalLink className="h-4 w-4 text-fog" /> View details
            </RouterLink>
            <button
              onClick={handleCopyLink}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <Link className="h-4 w-4 text-fog" /> Copy link
            </button>
          </div>
        </>
      )}
      {showQr && (
        <QrModal
          url={`${window.location.origin}/c/${combo.slug}`}
          title={combo.name}
          onClose={() => setShowQr(false)}
        />
      )}
    </motion.div>
  );
}
